import * as React from 'react'

import { ArrowUp, Camera, RefreshCw, X } from 'lucide-react'
import { observer } from 'mobx-react'

import Avatar from '~/client/src/shared/components/Avatar/Avatar'
import {
  FilePicker,
  IFilePickerActions,
} from '~/client/src/shared/components/FilePicker'
import {
  Attachment as AttachmentChip,
  AttachmentAction,
  AttachmentActions,
  AttachmentGroup,
  AttachmentMedia,
} from '~/client/src/shared/components/shadcn-ui/attachment'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import AvatarType from '~/client/src/shared/enums/AvatarType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { toast } from '~/client/src/shared/utils/toast'

import {
  activeQuery,
  editText,
  emptyDraft,
  insertMention,
  MentionDraft,
  moveSelection,
  toMarkup,
} from '../core/composer/mentionDraft'
import { LocalImageFile } from '../core/ports'
import { Attachment, Contact } from '../core/protocol/types'
import {
  AttachmentTrayStore,
  MAX_ATTACHMENTS,
  TrayItem,
} from '../core/stores/AttachmentTray.store'
import { isValidImageAttachment, toLocalImage } from '../web/imagePicker'

const INPUT_MAX_HEIGHT = 105

const CHIP_STATE: Record<TrayItem['state'], 'uploading' | 'done' | 'error'> = {
  uploading: 'uploading',
  uploaded: 'done',
  failed: 'error',
}

interface IProps {
  mentionCandidates: Contact[]
  tray: AttachmentTrayStore
  shouldSendOnEnter?: boolean
  onSend: (text: string, attachments: Attachment[]) => void
}

function ComposerBar({
  mentionCandidates,
  tray,
  shouldSendOnEnter,
  onSend,
}: IProps): React.ReactElement {
  const [draft, setDraft] = React.useState<MentionDraft>(emptyDraft)
  const inputRef = React.useRef<HTMLTextAreaElement>(null)
  const filePicker = React.useRef<IFilePickerActions>(null)
  const pendingCaret = React.useRef<number | null>(null)

  // The textarea grows with its content up to a cap, then scrolls. Below the
  // cap overflow stays hidden — height equals scrollHeight, so a scrollbar
  // there could only ever be a rounding phantom.
  React.useLayoutEffect(() => {
    const input = inputRef.current
    if (input) {
      input.style.height = 'auto'
      input.style.height = `${Math.min(input.scrollHeight, INPUT_MAX_HEIGHT)}px`
      input.style.overflowY =
        input.scrollHeight > INPUT_MAX_HEIGHT ? 'auto' : 'hidden'
    }
  }, [draft.text])

  React.useLayoutEffect(() => {
    const input = inputRef.current
    if (input && pendingCaret.current !== null) {
      input.setSelectionRange(pendingCaret.current, pendingCaret.current)
      pendingCaret.current = null
    }
  }, [draft])

  const canSend = (!!draft.text.trim() || tray.hasUploaded) && !tray.isUploading

  const query = activeQuery(draft)
  const needle = query?.text.toLowerCase()
  const suggestions =
    needle === undefined
      ? []
      : mentionCandidates.filter(candidate =>
          candidate.fullName.toLowerCase().includes(needle),
        )

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nextText = event.target.value
    setDraft(current => editText(current, nextText))
  }

  const handleSelect = (event: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const { selectionStart, selectionEnd } = event.currentTarget
    setDraft(current => moveSelection(current, selectionStart, selectionEnd))
  }

  const handlePickMention = (candidate: Contact) => {
    const next = insertMention(draft, candidate.id, candidate.fullName)
    pendingCaret.current = next.selection.start
    setDraft(next)
    inputRef.current?.focus()
  }

  const handleSend = () => {
    if (!canSend) {
      return
    }
    const message = toMarkup(draft)
    const attachments = tray.takeUploaded()
    setDraft(emptyDraft())
    onSend(message, attachments)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      shouldSendOnEnter &&
      event.key === 'Enter' &&
      !event.shiftKey &&
      !suggestions.length
    ) {
      event.preventDefault()
      handleSend()
    }
  }

  const handleAttachClick = () => {
    if (!tray.remainingSlots) {
      toast.info(
        Localization.translator.youCanAttachUpToXPhotos(MAX_ATTACHMENTS),
      )
      return
    }
    filePicker.current?.open()
  }

  const handleFilesSelected = async (files: File[]) => {
    const images = (
      await Promise.all(files.filter(isValidImageAttachment).map(toLocalImage))
    ).filter((image): image is LocalImageFile => !!image)

    const rejectedCount = files.length - images.length
    if (rejectedCount) {
      toast.info(
        Localization.translator.xFilesCouldNotBeAttached(rejectedCount),
      )
    }
    if (images.length) {
      tray.add(images)
    }
  }

  const renderSuggestions = (): React.ReactElement => (
    <div className="tw:max-h-44 tw:overflow-y-auto tw:border-0 tw:border-t tw:border-solid tw:border-border tw:bg-background">
      {suggestions.map(candidate => (
        <div
          key={candidate.id}
          className="tw:flex tw:cursor-pointer tw:items-center tw:gap-2.5 tw:px-4 tw:py-2.5"
          onMouseDown={event => {
            event.preventDefault()
            handlePickMention(candidate)
          }}
        >
          <Avatar avatarType={AvatarType.User} src={candidate.avatarUrl} />
          <span className="tw:text-[15px] tw:text-foreground">
            {candidate.fullName}
          </span>
        </div>
      ))}
    </div>
  )

  const renderChips = (): React.ReactElement => (
    <div className="tw:border-0 tw:border-t tw:border-solid tw:border-border tw:bg-muted tw:px-4 tw:pt-2.5">
      <AttachmentGroup>
        {tray.items.map(item => (
          <AttachmentChip
            key={item.id}
            state={CHIP_STATE[item.state]}
            orientation="vertical"
          >
            <AttachmentMedia variant="image">
              <img src={item.file.uri} alt={item.file.fileName} />
            </AttachmentMedia>
            <AttachmentActions>
              {item.state === 'failed' && (
                <AttachmentAction onClick={() => tray.retry(item.id)}>
                  <RefreshCw />
                </AttachmentAction>
              )}
              <AttachmentAction onClick={() => tray.remove(item.id)}>
                <X />
              </AttachmentAction>
            </AttachmentActions>
          </AttachmentChip>
        ))}
      </AttachmentGroup>
    </div>
  )

  return (
    <div className="tw:flex tw:flex-col">
      {!!suggestions.length && renderSuggestions()}
      {!!tray.items.length && renderChips()}
      <div
        className={`tw:flex tw:items-end tw:gap-2.5 tw:bg-muted tw:px-4 tw:pb-4 tw:pt-2.5 ${
          tray.items.length
            ? ''
            : 'tw:border-0 tw:border-t tw:border-solid tw:border-border'
        }`}
      >
        {/* The row is bottom-aligned so the buttons follow a growing
            textarea; 3px lifts the 32px buttons to the vertical center of
            the 38px single-line pill (20px line + 16px padding + borders). */}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleAttachClick}
          className="tw:mb-[3px]"
        >
          <Camera />
        </Button>
        <FilePicker
          actionsRef={filePicker}
          accept="image/*"
          multiple
          onPick={handleFilesSelected}
        />
        {/* The pill is a wrapper because scrollbars render at the element's
            box edge, outside a border-radius curve; the wrapper's overflow
            clip is what keeps the bar inside the rounding. */}
        <div className="tw:flex tw:min-w-0 tw:flex-1 tw:overflow-hidden tw:rounded-[20px] tw:border tw:border-solid tw:border-border tw:bg-background">
          <textarea
            ref={inputRef}
            value={draft.text}
            onChange={handleChange}
            onSelect={handleSelect}
            onKeyDown={handleKeyDown}
            rows={1}
            placeholder={Localization.translator.message_noun}
            className="tw:min-w-0 tw:flex-1 tw:resize-none tw:border-0 tw:bg-transparent tw:px-3.5 tw:py-2 tw:text-[15px] tw:leading-5 tw:text-foreground tw:outline-none tw:placeholder:text-muted-foreground"
          />
        </div>
        <Button
          size="icon"
          disabled={!canSend}
          onClick={handleSend}
          className="tw:mb-[3px] tw:rounded-full"
        >
          <ArrowUp className="tw:size-5" />
        </Button>
      </div>
    </div>
  )
}

export default observer(ComposerBar)
